import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams, useLocation } from 'react-router-dom';
import PublicLayout from '@/components/layout/PublicLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { welcomeWeekService, type WelcomeWeekAdminContext } from '@/services/welcomeWeekService';
import { DEFAULT_WW_SEASON_CODE, type WelcomeWeekAdminCalendarSource } from '@/types/welcomeWeek';
import { buildFullviewEditUrl, buildSourceFullviewUrl, seasonQuery } from './wwUi';
import { ArrowLeft, CalendarDays, ExternalLink, GraduationCap, Pencil, Users } from 'lucide-react';
import { toast } from 'sonner';
import GroupEventEditor from './GroupEventEditor';
import SourceEventEditor from './SourceEventEditor';

export default function WelcomeWeekAdminPage() {
  const { slug } = useParams<{ slug: string }>();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const seasonCode = searchParams.get('season') || DEFAULT_WW_SEASON_CODE;
  const explicitSeason = searchParams.get('season');
  const [context, setContext] = useState<WelcomeWeekAdminContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [masterPin, setMasterPin] = useState<string | null>(null);
  const [sources, setSources] = useState<WelcomeWeekAdminCalendarSource[]>([]);

  const isOmega2Variant = pathname.endsWith('/admin2');
  const effectiveSlug = isOmega2Variant ? 'omega2' : slug;

  const canManage = useMemo(() => context?.role === 'owner' || context?.role === 'admin', [context?.role]);
  // A configured Fadderprogram source is the master-editing mode. This makes
  // source editing a per-season configuration choice instead of a frontend map.
  const fadderSource = useMemo(() => sources.find((source) => source.source_key === 'fadder'), [sources]);
  const centralSource = useMemo(() => sources.find((source) => source.source_key === 'central'), [sources]);
  const orgName = isOmega2Variant ? 'Omega2' : (context?.organization.name || 'Master');

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      let adminContext = await welcomeWeekService.getAdminContext(slug, seasonCode);
      if (!adminContext.season && (adminContext.role === 'owner' || adminContext.role === 'admin')) {
        const season = await welcomeWeekService.ensureSeason(adminContext.organization.id, seasonCode);
        adminContext = { ...adminContext, season };
      }
      setContext(adminContext);
      if (adminContext.season) {
        try {
          setSources(await welcomeWeekService.getMyCalendarSources(slug, seasonCode, effectiveSlug));
        } catch (err) {
          console.error('[WelcomeWeekAdmin] failed to fetch calendar sources', err);
          setSources([]);
        }
        try {
          const access = await welcomeWeekService.getMyEditAccess(effectiveSlug, seasonCode);
          setMasterPin(access.masterPin);
        } catch (err) {
          console.error('[WelcomeWeekAdmin] failed to fetch edit access pins', err);
        }
      }
    } catch (error) {
      console.error('[WelcomeWeekAdmin] load failed', error);
      toast.error(error instanceof Error ? error.message : 'Failed to load welcome-week admin');
    } finally {
      setLoading(false);
    }
  }, [seasonCode, slug, effectiveSlug]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PublicLayout>
      <div className="container py-8 space-y-6">
        <div>
          <Button asChild variant="ghost" className="px-0">
            <Link to={`/o/${slug}/ww${seasonQuery(explicitSeason)}`}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Welcome Week
            </Link>
          </Button>
        </div>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{seasonCode}</Badge>
              {context?.role && <Badge variant="outline">{context.role}</Badge>}
            </div>
            <h1 className="mt-2 text-3xl font-bold tracking-tight">
              {orgName} Welcome Week Schedule
            </h1>
            <p className="text-muted-foreground">{orgName}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to={`/o/${slug}/ww/admin/groups${seasonQuery(explicitSeason, isOmega2Variant ? 'variant=omega2' : undefined)}`}>
                <Users className="h-4 w-4 mr-2" />
                Groups
              </Link>
            </Button>
            {centralSource && !isOmega2Variant && (
              <Button asChild variant="outline">
                <Link to={`/o/${slug}/ww/admin/central${seasonQuery(explicitSeason)}`}>
                  <GraduationCap className="h-4 w-4 mr-2" />
                  {centralSource.label}
                </Link>
              </Button>
            )}

            <Button asChild>
              <a
                href={`https://www.calone.com/${effectiveSlug}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <CalendarDays className="h-4 w-4 mr-2" />
                {orgName} Welcome Week Calendar
              </a>
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <CalendarDays className="h-10 w-10 animate-pulse text-muted-foreground" />
          </div>
        ) : !canManage ? (
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-muted-foreground">Owner or admin access is required.</p>
            </CardContent>
          </Card>
        ) : !fadderSource && !masterPin ? (
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-muted-foreground">Could not load master edit access. Try refreshing.</p>
            </CardContent>
          </Card>
        ) : fadderSource ? (
          <>
            {/* The configured Fadderprogram source regenerates the merged master
                whenever it is saved. A configured central source has its own
                admin page. */}
            <Card className="overflow-hidden">
              <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Pencil className="h-4 w-4" />
                  Edit week view ({orgName})
                  <Badge variant="secondary" className="ml-1">Editing</Badge>
                </div>
                <Button asChild variant="ghost" size="sm">
                  <a href={buildSourceFullviewUrl(fadderSource.calendar_username)} target="_blank" rel="noopener noreferrer">
                    Open in new window
                    <ExternalLink className="h-3.5 w-3.5 ml-1.5 opacity-70" />
                  </a>
                </Button>
              </div>
              <iframe
                title={`${fadderSource.label} editable full week view`}
                src={buildSourceFullviewUrl(fadderSource.calendar_username)}
                className="w-full bg-background"
                style={{ height: 949, border: 0 }}
                loading="lazy"
              />
            </Card>

            {/* In-sync list editor — reads/writes the same Fadderprogram source
                JSON the fullview above renders; saving regenerates the master. */}
            <SourceEventEditor
              season={seasonCode}
              stem={fadderSource.calendar_stem}
              label={fadderSource.label}
              orgSlug={effectiveSlug}
            />
          </>
        ) : (
          <>
            {/* Editable full-week view of the master programme */}
            <Card className="overflow-hidden">
              <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Pencil className="h-4 w-4" />
                  Edit week view (Master)
                  <Badge variant="secondary" className="ml-1">Editing</Badge>
                </div>
                <Button asChild variant="ghost" size="sm">
                  <a href={buildFullviewEditUrl('master', masterPin, slug)} target="_blank" rel="noopener noreferrer">
                    Open in new window
                    <ExternalLink className="h-3.5 w-3.5 ml-1.5 opacity-70" />
                  </a>
                </Button>
              </div>
              <iframe
                title="Master schedule editable full week view"
                src={buildFullviewEditUrl('master', masterPin, slug)}
                className="w-full bg-background"
                style={{ height: 949, border: 0 }}
                loading="lazy"
              />
            </Card>

            {/* In-sync list editor — reads/writes the same master CalOne JSON the
                fullview above renders, identical UX to the group editor. */}
            <GroupEventEditor
              kind="master"
              season={seasonCode}
              code="master"
              slug="master"
              pin={masterPin!}
            />
          </>
        )}
      </div>
    </PublicLayout>
  );
}
